'use client'

import { usePathname } from 'next/navigation'
import { Check, ChevronRight } from 'lucide-react'
import { formatDateNormal, formatImageURL } from '@/utils/util'
import { getReadingTime, getExcerpt } from '@/utils/blog'
import { navigateUrl } from '@/utils/client-request'
import BlogCard from './BlogCard'

export default function BlogList({ blogs, vendor }: { blogs: any[], vendor?: any }) {
  const pathname = usePathname() || ''
  const sorted = [...(blogs || [])].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
  const total = sorted.length

  if (total === 0) {
    return (
      <section className="bg-cream pt-6 pb-16">
        <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
          <nav className="text-[13px] text-foreground/55 mb-6">
            <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
            <span className="mx-1.5">/</span>
            <span className="text-foreground">Нийтлэл</span>
          </nav>
          <div className="rounded-3xl border border-dashed border-foreground/15 bg-[#F1EEE7]/40 py-20 px-8 text-center">
            <div className="text-lg font-semibold text-foreground">Нийтлэл олдсонгүй</div>
            <div className="mt-1.5 text-[14px] text-foreground/55">Яг одоогоор ямар нэгэн нийтлэл байхгүй байна. Хэсэг хугацааны дараа дахин шалгана уу.</div>
          </div>
        </div>
      </section>
    )
  }

  const lead = sorted[0]
  const secondary = sorted.slice(1, 3)
  const latest = sorted.slice(3, 9)
  const vendorName = vendor?.name || 'Shop.mn'

  return (
    <section className="bg-cream pt-6 pb-16">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10">
        <nav className="text-[13px] text-foreground/55 mb-8">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <span className="mx-1.5">/</span>
          <span className="text-foreground">Нийтлэл</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
          <div className="lg:col-span-8">
            <h1 className="mt-3 font-bold font-gilroy tracking-[-0.025em] leading-[0.96] text-foreground" style={{ fontSize: 'clamp(2.5rem, 6vw, 5.5rem)' }}>
              Худалдан авалтын
              <span className="block text-gradient">шинэ урлаг.</span>
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pb-4">
            <p className="text-[15px] sm:text-base text-foreground/65 leading-7 max-w-md">
              {vendor?.description || 'Бид блог хэсэгтээ худалдаа, хэрэглээ, зах зээлийн чиг хандлага болон бусад сонирхолтой сэдвүүдийг хамарсан нийтлэлүүдийг тогтмол нийтэлж байна.'}
            </p>
            <div className="mt-5 flex items-center gap-3 text-[12px] text-foreground/55">
              <span className="inline-flex items-center gap-1.5">
                <Check className="size-3.5 text-emerald-600" strokeWidth={3} /> Танд үргэлж шинэ нийтлэлүүдийг хүргэх болно
              </span>
              <span className="w-0.5 h-0.5 rounded-full bg-foreground/40" />
              <span>{total} нийтлэл</span>
            </div>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <a href={navigateUrl(`/blog/${lead.id}`, pathname)} className="lg:col-span-2 group relative rounded-3xl overflow-hidden cursor-pointer aspect-[16/10] sm:aspect-[16/8] bg-foreground block">
            <img src={formatImageURL(lead.thumbnail || 'placeholder.png')} alt={lead.title} className="absolute inset-0 w-full h-full object-cover transition-opacity duration-500 group-hover:opacity-90" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/10" />
            <div className="absolute inset-0 p-6 sm:p-10 lg:p-12 flex flex-col justify-end">
              <span className="inline-flex w-fit items-center gap-1.5 h-8 px-3 rounded-full bg-white/15 backdrop-blur-md text-[12px] font-medium text-white">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-300" /> Онцлох нийтлэл
              </span>
              <h2 className="mt-4 text-white font-bold font-gilroy tracking-[-0.02em] leading-[1.05] max-w-3xl" style={{ fontSize: 'clamp(1.75rem, 3.6vw, 3.25rem)' }}>
                {lead.title}
              </h2>
              {getExcerpt(lead.content || '', 180) && (
                <p className="mt-4 text-white/85 text-base sm:text-lg max-w-2xl leading-7 line-clamp-2">{getExcerpt(lead.content || '', 180)}</p>
              )}
              <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-[13px] text-white/75">
                <span>{formatDateNormal(new Date(lead.createdAt))}</span>
                <span className="w-1 h-1 rounded-full bg-white/40" />
                <span>{getReadingTime(lead.content || '')} мин унших</span>
                <span className="ml-auto hidden sm:inline-flex h-11 px-5 items-center gap-1.5 rounded-full bg-white text-foreground text-[14px] font-semibold">
                  Унших <ChevronRight className="size-4" />
                </span>
              </div>
            </div>
          </a>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-6">
            {secondary.map(p => (
              <a key={p.id} href={navigateUrl(`/blog/${p.id}`, pathname)} className="group cursor-pointer flex flex-col">
                <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-[#F1EEE7]">
                  <img src={formatImageURL(p.thumbnail || 'placeholder.png')} alt={p.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]" />
                </div>
                <h3 className="mt-4 text-[20px] font-bold font-gilroy tracking-[-0.015em] leading-[1.15] text-foreground group-hover:underline">
                  {p.title}
                </h3>
                {getExcerpt(p.content || '', 140) && (
                  <p className="mt-2 text-[13px] text-foreground/60 leading-6 line-clamp-2">{getExcerpt(p.content || '', 140)}</p>
                )}
                <div className="mt-3 text-[12px] text-foreground/55">
                  {formatDateNormal(new Date(p.createdAt))} · {getReadingTime(p.content || '')} мин
                </div>
              </a>
            ))}
          </div>
        </div>

        {latest.length > 0 && (
          <div className="mt-16">
            <div className="flex items-end justify-between gap-4 mb-7">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-foreground/55">Шинэ нийтлэлүүд</div>
                <h2 className="mt-1.5 font-bold font-gilroy tracking-[-0.015em] leading-[1.05] text-foreground" style={{ fontSize: 'clamp(1.5rem, 2.6vw, 2.25rem)' }}>
                  Сүүлд нэмэгдсэн
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-7 gap-y-12">
              {latest.map(p => <BlogCard key={p.id} blog={p} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  )
}
